import { cpfValidator } from 'helpers/cpfValidator';
import { Dispatch, SetStateAction, useState } from 'react';
import * as yup from 'yup';
import { cnpjValidator } from 'helpers/cnpjValidator';
import { Supplier } from 'types/supplier';
import { sanitizeDocument } from 'helpers/sanitizeDocument';

export type SupplierValidation = {
  document_number?: string;
  company_name?: string;
  company_trade_name?: string;
  person_type?: string;
};

type UseSupplierValidation = [
  SupplierValidation,
  Dispatch<SetStateAction<SupplierValidation>>,
  (supplier: Supplier) => Promise<void>,
];

export function useSupplierValidation(): UseSupplierValidation {
  async function handleValidation(supplier: Supplier) {
    const schema = yup.object().shape({
      document_number: yup
        .string()
        .transform((value, originalValue) => {
          return sanitizeDocument(originalValue);
        })
        .required('O CPF/CNPJ é obrigatório')
        .test('is-valid-cpf-cnpj', 'CPF/CNPJ inválido', value => {
          if (!value) return false;

          const cnpj = cnpjValidator(value);
          const cpf = cpfValidator(value);

          return cnpj || cpf;
        }),
      person_type: yup.string().required('O tipo de pessoa é obrigatório'),
      company_trade_name: yup
        .string()
        .required('O nome fantasia é obrigatório')
        .min(10, 'O nome fantasia deve ter no mínimo 10 caracteres'),
      company_name: yup
        .string()
        .required('A razão social é obrigatória')
        .min(10, 'A razão social deve ter no mínimo 10 caracteres'),
    });

    try {
      await schema.validate(supplier);
    } catch (err) {
      const error = err as yup.ValidationError;
      if (error.path && error.message) {
        setValidation({
          [error.path]: error.message,
        });
      }
      throw new Error(err as string);
    }
  }

  const [validation, setValidation] = useState<SupplierValidation>({});
  return [validation, setValidation, handleValidation];
}
